import { Box } from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef } from "react";

export const DragContext = React.createContext({});

/**
 * The drag context provider
 * 
 * @param {Object} props The props of the component
 * @returns The drag context provider
 */
const DragProvider = ({ children }) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [position, setPosition] = React.useState({
    x: 150,
    y: 100,
  });
  const offset = useRef({ x: 0, y: 0 });
  const boxRef = useRef();

  /**
   * Handles the drag start
   * 
   * @param {Object} event The event object
   */
  const onDragStart = useCallback(
    (event) => {
      const touch = event?.touches?.[0];
      const startX = (event.clientX || touch?.clientX || 0).toFixed();
      const startY = (event.clientY || touch?.clientY || 0).toFixed();

      offset.current.x = -startX - position.x;
      offset.current.y = startY - position.y;
      setIsDragging(true);
    },
    [position],
  );

  /**
   * Handles the drag move
   * 
   * @param {Object} event The event object
   */
  const onDragMove = useCallback(
    (event) => {
      const touch = event.touches?.[0];
      const moveX = (event.clientX || touch?.clientX || 0).toFixed();
      const moveY = (event.clientY || touch?.clientY || 0).toFixed();

      event.preventDefault();
      setPosition({
        x: Math.min(
          -moveX - offset.current.x,
          window.innerWidth - boxRef.current.offsetWidth,
        ),
        y: Math.max(moveY - offset.current.y, 0),
      });
    },
    [offset],
  );

  /**
   * Handles the drag end
   */
  const onDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", onDragMove);
      window.addEventListener("touchmove", onDragMove);
      window.addEventListener("mouseup", onDragEnd);
      window.addEventListener("touchend", onDragEnd);
      return () => {
        window.removeEventListener("mousemove", onDragMove);
        window.removeEventListener("touchmove", onDragMove);
        window.removeEventListener("mouseup", onDragEnd);
        window.removeEventListener("touchend", onDragEnd);
      };
    }
  }, [isDragging, onDragEnd, onDragMove]);

  return (
    <DragContext.Provider value={{ isDragging, onDragStart }}>
      <Box
        position={"absolute"}
        display={"flex"}
        top={`${position.y}px`}
        right={`${position.x}px`}
        zIndex={1300}
        ref={boxRef}
      >
        {children}
      </Box>
    </DragContext.Provider>
  );
};

export default React.memo(DragProvider);
